
import React, { useState, useEffect, useRef } from 'react';
import { X, Lock, Loader2, ShieldCheck, CheckCircle2 } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { useToast } from '../contexts/ToastContext';
import { api } from '../lib/db';

interface PaymentModalProps {
  planName: string;
  price: number;
  credits: number;
  onClose: () => void;
  onConfirm: (details: any) => Promise<void>; 
  theme: 'light' | 'dark';
  onSuccess?: () => void; // Opsiyonel: Ödeme başarılı olduğunda çağrılır
}

const PaymentModal: React.FC<PaymentModalProps> = ({ planName, price, credits, onClose, onConfirm, theme, onSuccess }) => {
  const { t } = useLanguage();
  const { addToast } = useToast();
  const [loading, setLoading] = useState(true);
  const [iframeToken, setIframeToken] = useState<string | null>(null);
  const [merchantOid, setMerchantOid] = useState<string | null>(null);
  const [error, setError] = useState('');
  const iframeRef = useRef<HTMLIFrameElement>(null);

  // Initialize PayTR when modal opens
  useEffect(() => {
    const initPayment = async () => {
      try {
        setLoading(true);
        // Call the parent handler which calls the API /payment/init
        await onConfirm({
            isPayTR: true
        }).then((response: any) => {
            if (response) {
                if (response.token) setIframeToken(response.token);
                if (response.merchant_oid) setMerchantOid(response.merchant_oid);
            }
        });
        
      } catch (err: any) {
        // If it's a specific PayTR error, show it, otherwise generic
        setError(err.message || 'Ödeme başlatılamadı.');
      } finally {
        setLoading(false);
      }
    };

    initPayment();
  }, []);

  // Handle PayTR Iframe Resizing script injection and payment success
  useEffect(() => {
    if (iframeToken && iframeToken !== 'MOCK_TOKEN_DEV_MODE') {
        const script = document.createElement('script');
        script.src = "https://www.paytr.com/js/iframeResizer.min.js";
        script.async = true;
        document.body.appendChild(script);

        const handleResize = () => {
            // @ts-ignore
            if (window.iFrameResize && iframeRef.current) {
                // @ts-ignore
                window.iFrameResize({}, '#paytriframe');
            }
        };

        script.onload = handleResize;

        // PayTR iframe'den gelen mesajları dinle
        const handleMessage = (event: MessageEvent) => {
            // PayTR'den gelen başarı mesajını kontrol et
            if (event.data && typeof event.data === 'string' && event.data.includes('success')) {
                if (onSuccess) {
                    onSuccess();
                } else {
                    // Varsayılan davranış: sayfayı yenile
                    setTimeout(() => window.location.reload(), 1000);
                }
            }
        };

        // URL değişikliğini dinle (PayTR başarılı ödeme sonrası yönlendirme)
        const checkUrl = () => {
            if (window.location.pathname.includes('/payment/success')) {
                if (onSuccess) {
                    onSuccess();
                }
            }
        };

        window.addEventListener('message', handleMessage);
        const urlCheckInterval = setInterval(checkUrl, 500);

        return () => {
            if(document.body.contains(script)) {
                document.body.removeChild(script);
            }
            window.removeEventListener('message', handleMessage);
            clearInterval(urlCheckInterval);
        };
    }
  }, [iframeToken, onSuccess]);

  const handleSimulateSuccess = async () => {
      if (!merchantOid) {
          addToast('Hata: İşlem ID bulunamadı.', 'error');
          return;
      }

      setLoading(true);
      try {
          // Call backend to forcefully complete the mock payment in the DB
          await api.transactions.completeMock(merchantOid);
          addToast('Test Ödemesi Başarıyla Tamamlandı!', 'success');
          onClose();
          
          // Eğer onSuccess callback'i varsa onu çağır, yoksa sayfayı yenile
          if (onSuccess) {
              onSuccess();
          } else {
              // Reload to refresh credits in the UI
              setTimeout(() => window.location.reload(), 1000);
          }
      } catch (err: any) {
          addToast('Mock işlem hatası: ' + err.message, 'error');
      } finally {
          setLoading(false);
      }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-gray-900/90 backdrop-blur-sm p-2 sm:p-4 animate-in fade-in duration-200">
      <div className="bg-white dark:bg-gray-800 rounded-2xl w-full max-w-2xl max-h-[90vh] shadow-2xl overflow-hidden relative flex flex-col">
        {/* Header */}
        <div className="flex justify-between items-center p-4 border-b border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50">
            <div className="flex items-center gap-3">
                <ShieldCheck className="w-6 h-6 text-green-600"/>
                <div>
                    <h3 className="font-bold text-gray-900 dark:text-white">Güvenli Ödeme</h3>
                    <p className="text-xs text-gray-500">{planName} - <span className="font-bold text-indigo-600">{price} ₺</span></p>
                </div>
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-white transition-colors">
                <X className="w-6 h-6"/>
            </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto bg-white dark:bg-gray-800 relative min-h-[400px]">
            {loading && (
                <div className="absolute inset-0 flex flex-col items-center justify-center z-20 bg-white dark:bg-gray-800">
                    <Loader2 className="w-10 h-10 text-indigo-600 animate-spin mb-4"/>
                    <p className="text-gray-500 font-medium">İşlem yapılıyor...</p>
                </div>
            )}

            {error ? (
                <div className="flex flex-col items-center justify-center h-full p-8 text-center">
                    <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
                        <X className="w-8 h-8 text-red-600"/>
                    </div>
                    <h4 className="text-lg font-bold text-gray-900 dark:text-white mb-2">Hata Oluştu</h4>
                    <p className="text-gray-500 text-sm mb-6">{error}</p>
                    <button onClick={onClose} className="px-6 py-2 bg-gray-200 dark:bg-gray-700 rounded-lg font-bold text-sm hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors">Kapat</button>
                </div>
            ) : iframeToken === 'MOCK_TOKEN_DEV_MODE' ? (
                // MOCK UI FOR DEVELOPMENT/TESTING
                <div className="flex flex-col items-center justify-center h-full p-8 text-center bg-yellow-50/50 dark:bg-yellow-900/10">
                    <div className="w-20 h-20 bg-yellow-100 dark:bg-yellow-900/30 rounded-full flex items-center justify-center mb-4 border-4 border-yellow-50 dark:border-yellow-800 animate-bounce">
                        <ShieldCheck className="w-10 h-10 text-yellow-600 dark:text-yellow-400"/>
                    </div>
                    <h4 className="text-xl font-black text-gray-900 dark:text-white mb-2">Test Modu Aktif</h4>
                    <p className="text-gray-600 dark:text-gray-300 text-sm mb-6 max-w-sm mx-auto leading-relaxed">
                        PayTR API anahtarları tanımlanmadığı için <strong>Test Modundasınız</strong>. 
                        Gerçek bir ödeme alınmayacaktır.
                    </p>
                    
                    <div className="bg-white dark:bg-gray-900 p-4 rounded-xl border border-gray-200 dark:border-gray-700 w-full max-w-xs mb-6 shadow-sm">
                        <div className="flex justify-between text-sm mb-2">
                            <span className="text-gray-500">Tutar:</span>
                            <span className="font-bold dark:text-white">{price} ₺</span>
                        </div>
                        <div className="flex justify-between text-sm">
                            <span className="text-gray-500">Paket:</span>
                            <span className="font-bold dark:text-white">{planName}</span>
                        </div>
                    </div>

                    <button 
                        onClick={handleSimulateSuccess} 
                        className="px-8 py-3 bg-green-600 hover:bg-green-700 text-white rounded-xl font-bold shadow-lg flex items-center transition-all transform hover:scale-105"
                    >
                        <CheckCircle2 className="w-5 h-5 mr-2" />
                        Ödemeyi Başarılı Say
                    </button>
                    <p className="text-xs text-gray-400 mt-4">Bu işlem veritabanına kredi ekler.</p>
                </div>
            ) : iframeToken ? (
                <iframe
                    ref={iframeRef}
                    src={`https://www.paytr.com/odeme/guvenli/${iframeToken}`}
                    id="paytriframe"
                    className="w-full min-h-[600px] border-none"
                    title="PayTR Ödeme"
                ></iframe>
            ) : null}
        </div>
        
        {/* Footer */}
        <div className="p-4 bg-gray-50 dark:bg-gray-900/50 border-t border-gray-100 dark:border-gray-700 text-center space-y-3">
            <img 
                src="/paytr_logo.png" 
                alt="PayTR Ödeme Altyapısı" 
                className="mx-auto h-8 w-auto"
            />
            <p className="text-xs text-gray-400 flex items-center justify-center"><Lock className="w-3 h-3 mr-1"/>256-bit SSL Korumalı Güvenli Ödeme</p>
        </div>
      </div>
    </div>
  );
};

export default PaymentModal;
